<template>
  <div
    class="preview-img-list"
    :class="gridClass"
  >
    <figure
      v-for="(item, index) in visibleItems"
      :key="index"
      class="gallery-item"
      :class="{ 'more-overlay': isOverlayItem(index) }"
      @click="handleClick(index)"
    >
      <img
        class="preview-img-item"
        :src="item.preview"
      >
      <div
        v-if="isOverlayItem(index)"
        class="overlay"
      >
        <span class="overlay-count">+{{ hiddenCount }}</span>
      </div>
      <figcaption v-if="item.title && !isOverlayItem(index)">
        {{ item.title }}
      </figcaption>
    </figure>
  </div>
</template>

<script>
export default {
  props: ['images', 'mediaCategory'],

  data() {
    return {
      maxVisible: 6
    }
  },

  computed: {
    items() {
      return this.images.map((image) => {
        return this.getImageObject(image)
      })
    },

    visibleItems() {
      if (this.items.length <= this.maxVisible) {
        return this.items
      }
      return this.items.slice(0, this.maxVisible)
    },

    hiddenCount() {
      return this.items.length - this.maxVisible
    },

    hasMoreItems() {
      return this.items.length > this.maxVisible
    },

    gridClass() {
      const count = this.visibleItems.length
      if (count === 1) return 'grid-1'
      if (count === 2) return 'grid-2'
      if (count === 3) return 'grid-3'
      if (count === 4) return 'grid-4'
      return 'grid-many'
    }
  },

  methods: {
    getImageObject(image) {
      const title = image.meta ? image.meta.displayName : ''
      const description = image.meta ? image.meta.description : ''

      if (!image.availableSizes || !image.availableSizes.length) {
        return {
          src: image.path,
          w: 1200,
          h: 800,
          title,
          description,
          preview: image.path
        }
      }

      const largeVariant = `${this.mediaCategory ? this.mediaCategory : 'post'}-xl`
      const previewVariant = `${this.mediaCategory ? this.mediaCategory : 'post'}-m`

      const imageObject = image.availableSizes.find(size => size.name === largeVariant)
      const previewObject = image.availableSizes.find(size => size.name === previewVariant)

      return {
        src: imageObject.path,
        w: imageObject.width,
        h: imageObject.height,
        title,
        description,
        preview: previewObject.path
      }
    },

    escapeHtml(text) {
      return String(text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
    },

    // PhotoSwipe's default UI injects item.title as innerHTML into the
    // caption, so title + description travel as one escaped HTML block
    captionHtml(item) {
      const parts = []
      if (item.title) parts.push(`<div class="pswp-caption-title">${this.escapeHtml(item.title)}</div>`)
      if (item.description) parts.push(`<div class="pswp-caption-description">${this.escapeHtml(item.description)}</div>`)
      return parts.join('')
    },

    isOverlayItem(index) {
      return this.hasMoreItems && index === this.maxVisible - 1
    },

    handleClick(index) {
      // If clicking the overlay item, open PhotoSwipe at that position
      // This allows users to swipe through ALL images including hidden ones
      const slides = this.items.map(item => ({ ...item, title: this.captionHtml(item) }))
      this.$photoswipe.open(index, slides)
    }
  }
}
</script>

<style lang="scss">
// PhotoSwipe mounts its root on <body>, out of reach of scoped styles
.pswp__caption__center {
  text-align: center;
}

.pswp-caption-title {
  font-size: 0.9rem;
  color: #fff;
}

.pswp-caption-description {
  margin-top: 2px;
  font-size: 0.8rem;
  color: #ccc;
}
</style>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.preview-img-list {
  display: grid;
  gap: 8px;
  width: 100%;

  // Single image - full width
  &.grid-1 {
    grid-template-columns: 1fr;
  }

  // Two images - side by side
  &.grid-2 {
    grid-template-columns: repeat(2, 1fr);
  }

  // Three images - first large, two smaller on right
  &.grid-3 {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);

    .gallery-item:first-child {
      grid-row: span 2;
    }
  }

  // Four images - 2x2 grid
  &.grid-4 {
    grid-template-columns: repeat(2, 1fr);
  }

  // 5+ images - responsive grid
  &.grid-many {
    grid-template-columns: repeat(3, 1fr);

    @media screen and (max-width: $mobile) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

.gallery-item {
  position: relative;
  margin: 0 !important;
  overflow: hidden;
  border-radius: 4px;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.2s ease;
  }

  &:hover img {
    transform: scale(1.02);
  }

  figcaption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 8px 12px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    color: white;
    font-size: 0.85rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover figcaption {
    opacity: 1;
  }

  // Overlay for "+X more" indicator
  &.more-overlay {
    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease;
    }

    .overlay-count {
      color: white;
      font-size: 1.75rem;
      font-weight: 600;
    }

    &:hover .overlay {
      background: rgba(0, 0, 0, 0.65);
    }
  }
}

// Make images in grid-3 layout fill their cells properly
.grid-3 .gallery-item {
  aspect-ratio: 1;

  &:first-child {
    aspect-ratio: auto;
  }
}

// Consistent aspect ratio for grid layouts with 4+
.grid-4 .gallery-item,
.grid-many .gallery-item {
  aspect-ratio: 1;
}
</style>
